import React from 'react';
import { Bell, Plus, Calendar, Clock } from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Header } from '@/components/Header';

const Reminders: React.FC = () => {
  const { tasks } = useApp();
  
  // Get tasks with reminders
  const tasksWithReminders = tasks.filter(task => task.reminder && !task.completed);

  const headerAction = (
    <Button 
      onClick={() => {}} 
      className="bg-gray-900 hover:bg-gray-800"
    >
      <Plus className="w-4 h-4 mr-2" />
      New Reminder
    </Button>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-gray-50">
      <Header 
        title="Reminders"
        subtitle={`${tasksWithReminders.length} active reminders`}
        action={headerAction}
      />
      
      <div className="p-4 md:p-6 max-w-7xl mx-auto mt-24">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                Reminders
              </h1>
              <p className="text-gray-500 mt-1">
                {tasksWithReminders.length} active {tasksWithReminders.length === 1 ? 'reminder' : 'reminders'}
              </p>
            </div>
          </div>

          {/* Reminders List */}
          <Card className="bg-white/50 backdrop-blur-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Active Reminders
              </CardTitle>
            </CardHeader>
            <CardContent>
              {tasksWithReminders.length > 0 ? (
                <div className="space-y-4">
                  {tasksWithReminders.map((task) => (
                    <Card key={task.id} className="bg-white">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-medium">{task.title}</h3>
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                <span>{new Date(task.dueDate!).toLocaleDateString()}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                <span>{new Date(task.reminder!).toLocaleTimeString()}</span>
                              </div>
                            </div>
                          </div>
                          <div className={cn(
                            "w-2 h-2 rounded-full",
                            task.category === 'Personal' ? 'bg-purple-500' :
                            task.category === 'Work' ? 'bg-blue-500' :
                            task.category === 'Health' ? 'bg-green-500' :
                            'bg-orange-500'
                          )} />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <Bell className="w-12 h-12 mx-auto mb-4 opacity-20" />
                  <p>No active reminders</p>
                  <p className="text-sm mt-1">Create a reminder to get notified about your tasks</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Reminders; 